import { useEffect, useRef } from 'react';

/*
 * Экран не должен дёргаться, когда содержимое под фильтром пересобирается.
 *
 * Смена выбора в фильтре удаляет и заново грузит блоки ниже, и страница на
 * мгновение становится короче. Если она прокручена, браузер вынужден подтянуть
 * scroll вверх, а когда блоки догрузятся — содержимое снова уезжает: фильтр
 * прыгает под курсором.
 *
 * Поэтому контейнер держит min-height до нижней границы экрана. Держим не
 * больше текущей высоты — страница сама не растёт; при прокрутке вверх
 * удержание ослабевает, и лишнее пустое место исчезает там, где его не видно.
 */
export const heldMinHeight = ({ height, top, scrollY, viewportHeight }) =>
  Math.max(0, Math.min(height, scrollY + viewportHeight - top));

export const useScrollStableHeight = () => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const apply = () => {
      const held = heldMinHeight({
        height: node.offsetHeight,
        top: node.getBoundingClientRect().top + window.scrollY,
        scrollY: window.scrollY,
        viewportHeight: window.innerHeight,
      });
      node.style.minHeight = held > 0 ? `${held}px` : '';
    };

    apply();
    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply);
    // Рост содержимого без прокрутки тоже двигает «низ экрана внутри контейнера».
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    if (observer) observer.observe(node);

    return () => {
      window.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      if (observer) observer.disconnect();
    };
  }, []);

  return ref;
};

export default useScrollStableHeight;
